import { useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { Sidebar } from './Sidebar';
import { TopBar } from './TopBar';
import { QuickJump } from './QuickJump';
import { ErrorBoundary } from '@/components/ErrorBoundary';

/** The authenticated app frame: sidebar + topbar + routed content. */
export function AppShell() {
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();

  return (
    <div className="flex h-dvh">
      <QuickJump />
      <Sidebar open={menuOpen} onClose={() => setMenuOpen(false)} />
      <div className="flex min-w-0 flex-1 flex-col">
        <TopBar onOpenMenu={() => setMenuOpen(true)} />
        <main className="flex-1 overflow-y-auto pb-[env(safe-area-inset-bottom)]">
          <div className="mx-auto w-full max-w-7xl px-4 py-6 lg:px-8 lg:py-8">
            <ErrorBoundary key={location.pathname}>
              <Outlet />
            </ErrorBoundary>
          </div>
        </main>
      </div>
    </div>
  );
}

/** Standard page header — editorial, like a report section opener: optional
 * uppercase kicker, a confident display title, and the SHORT gradient rule
 * (the report H1's underline — never a full-width stripe). */
export function PageHeader({
  title,
  kicker,
  description,
  action,
}: {
  title: string;
  kicker?: string;
  description?: string;
  action?: React.ReactNode;
}) {
  return (
    <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
      <div>
        {kicker && <p className="kicker mb-1.5">{kicker}</p>}
        <h1 className="text-[26px] font-semibold leading-tight">{title}</h1>
        <div className="grad-bar mt-3 h-[3px] w-14 rounded-full" aria-hidden />
        {description && <p className="mt-3 max-w-xl text-sm text-fg-muted">{description}</p>}
      </div>
      <div className="pb-1">{action}</div>
    </div>
  );
}
